import { useEffect, useState } from 'react';
import { ExternalLink, Loader2, MessagesSquare, ThumbsDown, ThumbsUp } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import InlineNotice from './InlineNotice';
import { Pill, RolePill } from './StatusBadge';
import { useHelpChatSession } from '../../hooks/admin/useAdminHelpChat';
import { moduleFromPath } from '../../lib/helpContext';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/utils';
import type { HelpChatLogRow } from '../../types/admin';

interface HelpChatLogDetailModalProps {
  log: HelpChatLogRow | null;
  onClose: () => void;
  onFilterBySession?: (sessionId: string) => void;
}

function Meta({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-glass-text-3">{label}</div>
      <div className="mt-0.5 truncate text-sm text-glass-text">{children ?? '—'}</div>
    </div>
  );
}

function FeedbackIcon({ feedback }: { feedback: HelpChatLogRow['feedback'] }) {
  if (feedback === 'up') return <ThumbsUp className="inline h-4 w-4 text-emerald-500" aria-label="Helpful" />;
  if (feedback === 'down') return <ThumbsDown className="inline h-4 w-4 text-rose-500" aria-label="Not helpful" />;
  return <span className="text-glass-text-3">none</span>;
}

export default function HelpChatLogDetailModal({ log, onClose, onFilterBySession }: HelpChatLogDetailModalProps) {
  const [showSession, setShowSession] = useState(false);
  useEffect(() => {
    setShowSession(false);
  }, [log?.id]);

  const sessionQuery = useHelpChatSession(log?.sessionId ?? null, showSession);
  const roleDir = (log?.role || 'agency').toLowerCase();

  return (
    <Modal
      isOpen={Boolean(log)}
      onClose={onClose}
      title="Chatbot conversation"
      size="xl"
      mobilePresentation="fullscreen"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {log?.sessionId && (
              <>
                <Button variant="outline" size="sm" onClick={() => setShowSession((v) => !v)}>
                  <MessagesSquare className="mr-2 h-4 w-4" aria-hidden />
                  {showSession ? 'Hide conversation' : 'View conversation'}
                </Button>
                {onFilterBySession && (
                  <Button variant="ghost" size="sm" onClick={() => onFilterBySession(log.sessionId as string)}>
                    Filter table by this session
                  </Button>
                )}
              </>
            )}
          </div>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {log && (
        <div className="space-y-5">
          <div className="rounded-xl border border-glass bg-glass-2 p-4">
            <div className="mb-1 text-[11px] uppercase tracking-wide text-glass-text-3">Question</div>
            <p className="whitespace-pre-wrap text-sm text-glass-text">{log.question}</p>
          </div>

          <div className="rounded-xl border border-glass bg-glass-1 p-4">
            <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-glass-text-3">
              Answer
              {log.answerWasFallback && <Pill tone="warn">fallback</Pill>}
            </div>
            <p className="whitespace-pre-wrap text-sm text-glass-text">{log.answer}</p>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-4">
            <Meta label="Asked">{formatDateTime(log.createdAt)}</Meta>
            <Meta label="User">
              {log.user ? (
                <span title={log.user.email ?? undefined}>{log.user.fullName || log.user.email || log.user.id}</span>
              ) : (
                log.userId || '—'
              )}
            </Meta>
            <Meta label="Organisation">
              {log.organization ? `${log.organization.name} (${log.organization.type})` : log.organizationId || '—'}
            </Meta>
            <Meta label="Role">
              <RolePill role={log.role} />
            </Meta>
            <Meta label="Module">
              <span title={log.route ?? undefined}>{log.route ? moduleFromPath(log.route) : '—'}</span>
            </Meta>
            <Meta label="Route">
              <span className="font-mono text-xs">{log.route || '—'}</span>
            </Meta>
            <Meta label="Provider · model">
              {log.provider || '—'}
              {log.model ? ` · ${log.model}` : ''}
            </Meta>
            <Meta label="Latency">{log.latencyMs != null ? `${log.latencyMs.toLocaleString()} ms` : '—'}</Meta>
            <Meta label="Tokens (prompt / completion)">
              {log.promptTokens != null || log.completionTokens != null
                ? `${(log.promptTokens ?? 0).toLocaleString()} / ${(log.completionTokens ?? 0).toLocaleString()}`
                : '—'}
            </Meta>
            <Meta label="Feedback">
              <FeedbackIcon feedback={log.feedback} />
            </Meta>
            <Meta label="Session">
              <span className="font-mono text-xs" title={log.sessionId ?? undefined}>
                {log.sessionId ? log.sessionId.slice(0, 12) : '—'}
              </span>
            </Meta>
            <Meta label="Highlight">
              <span className="font-mono text-xs">{log.highlightId || '—'}</span>
            </Meta>
          </div>

          {log.retrievedSources && log.retrievedSources.length > 0 && (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-glass-text-3">
                Retrieved sources ({log.retrievedSources.length})
              </div>
              <ul className="space-y-1 text-xs text-glass-text-2">
                {log.retrievedSources.map((s, i) => (
                  <li key={`${s}-${i}`} className="font-mono">
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {log.deepLinks && log.deepLinks.length > 0 && (
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-wide text-glass-text-3">Deep links offered</div>
              <div className="flex flex-wrap gap-2">
                {log.deepLinks.map((l) => (
                  <span
                    key={`${l.label}-${l.url}`}
                    className="inline-flex items-center gap-1 rounded-lg border border-glass bg-glass-1 px-2.5 py-1 text-xs text-glass-text"
                    title={l.url}
                  >
                    <ExternalLink className="h-3 w-3 text-glass-text-3" aria-hidden />
                    {l.label}
                    <span className="font-mono text-glass-text-3">{l.url}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {showSession && (
            <div className="rounded-xl border border-glass bg-glass-2 p-4">
              <div className="mb-3 text-sm font-semibold text-glass-text">Full conversation</div>
              {sessionQuery.isPending ? (
                <div className="flex items-center gap-2 text-sm text-glass-text-3">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…
                </div>
              ) : sessionQuery.isError ? (
                <InlineNotice tone="error">{getApiErrorMessage(sessionQuery.error, 'Failed to load the session')}</InlineNotice>
              ) : (
                <ol className="space-y-3">
                  {(sessionQuery.data?.turns ?? []).map((turn, i) => (
                    <li
                      key={turn.id}
                      className={`rounded-lg border p-3 ${turn.id === log.id ? 'border-accent2/50 bg-accent2/5' : 'border-glass bg-glass-1'}`}
                    >
                      <div className="mb-1 flex items-center justify-between text-[11px] text-glass-text-3">
                        <span>
                          Turn {i + 1} · {formatDateTime(turn.createdAt)}
                        </span>
                        <span className="flex items-center gap-2">
                          {turn.answerWasFallback && <Pill tone="warn">fallback</Pill>}
                          <FeedbackIcon feedback={turn.feedback} />
                        </span>
                      </div>
                      <p className="text-sm font-medium text-glass-text">{turn.question}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-glass-text-2">{turn.answer}</p>
                    </li>
                  ))}
                  {sessionQuery.data && sessionQuery.data.turns.length === 0 && (
                    <li className="text-sm text-glass-text-3">No other turns in this session.</li>
                  )}
                </ol>
              )}
            </div>
          )}

          <p className="rounded-lg border border-dashed border-glass p-3 text-xs text-glass-text-3">
            To improve this answer, edit the relevant document under{' '}
            <code className="font-mono text-glass-text-2">Backend/knowledge/docs/{roleDir}/</code> and re-index with{' '}
            <code className="font-mono text-glass-text-2">pnpm --dir Backend help:ingest</code>.
          </p>
        </div>
      )}
    </Modal>
  );
}
